import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'

import { PageCard, PageLayout } from '@/components/ui/page'
import {
	CellText,
	CellTextDate,
	DataTable,
	useDataTable,
} from '@/components/ui/table/data-table'

import { accountMasterList } from '@/features/account/api'
import {
	AccountStatusBadge,
	RoleBadge,
} from '@/features/account/components/common'
import type { IAccount } from '@/features/account/type'

export const Route = createFileRoute('/_app/settings/user/')({
	component: Component,
})

function Component() {
	return (
		<PageLayout.Root>
			<PageLayout.Header
				breadcrumbs={[
					['/settings', 'Pengaturan'],
					['/settings/user', 'Pengguna'],
				]}
			/>
			<PageCard.Root>
				<PageCard.Header
					title="Daftar Pengguna"
					actions={
						<PageCard.HeaderAction asChild>
							<Link from={Route.fullPath} to="/settings/user/create">
								Tambah
							</Link>
						</PageCard.HeaderAction>
					}
				/>
				<AccountTable />
			</PageCard.Root>
		</PageLayout.Root>
	)
}

function AccountTable() {
	const { data, isLoading } = useQuery(accountMasterList.qry())
	const columns = useMemo(getColumns, [])
	const { table } = useDataTable({
		columns,
		data: data?.accounts ?? [],
	})

	return <DataTable table={table} columns={columns} isLoading={isLoading} />
}

const columnHelper = createColumnHelper<IAccount>()

function getColumns() {
	return [
		columnHelper.accessor('name', {
			header: () => 'Name',
			cell: ({ row }) => (
				<Link
					from={Route.fullPath}
					to="/settings/user/$id"
					params={{ id: row.original.id }}
				>
					<CellText text={row.original.name} />
				</Link>
			),
		}),
		columnHelper.accessor('email', {
			header: () => 'Email',
			cell: ({ row }) => <CellText text={row.original.email} />,
		}),
		columnHelper.accessor('role', {
			header: () => 'Role',
			cell: ({ row }) => <RoleBadge role={row.original.role} />,
		}),
		columnHelper.accessor('status', {
			header: () => 'Status',
			cell: ({ row }) => <AccountStatusBadge status={row.original.status} />,
		}),
		columnHelper.accessor('createdAt', {
			header: () => 'Created At',
			cell: ({ row }) => <CellTextDate date={row.original.createdAt} />,
		}),
		columnHelper.accessor('updatedAt', {
			header: () => 'Updated At',
			cell: ({ row }) => <CellTextDate date={row.original.updatedAt} />,
		}),
	]
}
